import { datadogLogs } from '@datadog/browser-logs';
import { FC, useEffect, useRef, useState } from 'react';
import { cleanFileName, configVars } from 'src/helpers';
import { downloadDocument } from 'src/helpers/downloadUtils';
import ViewSDKClient from 'src/pdfViewer/ViewSDKClient';
import { IEntry, IProof } from 'src/ts/interfaces';
import DocumentViewerControls from '../DocumentViewerControls';
import {
  DocumentStatusMessage,
  DocumentSurface,
  ViewerControlsBelowHeader,
  ViewerSurface
} from './styles';

export const viewerContainerId = (proofId?: string) =>
  `inspector-pdf-viewer-container-${proofId ?? 'none'}`;

const VIEWER_CONFIG = {
  embedMode: 'FULL_WINDOW',
  defaultViewMode: 'FIT_WIDTH',
  showDownloadPDF: false,
  showPrintPDF: false,
  showAnnotationTools: false,
  showThumbnails: false,
  showBookmarks: false,
  showZoomControl: false,
  showPageControls: false
};

type ViewerStatus = 'loading' | 'ready' | 'error' | 'unavailable';

interface IProps {
  entry: IEntry;
  currentProof: IProof | undefined;
  showControls?: boolean;
}

const InspectorDocument: FC<IProps> = ({ entry, currentProof, showControls = true }) => {
  const viewSDKClientRef = useRef<ViewSDKClient | null>(null);
  const [status, setStatus] = useState<ViewerStatus>('loading');
  const applicantName = entry?.applicant?.full_name ?? '';
  const entryId = entry?.id;
  const file = currentProof?.file;
  const fileName = currentProof?.fileName;
  const proofId = currentProof?.id;

  useEffect(() => {
    viewSDKClientRef.current = null;

    if (!file) {
      setStatus('unavailable');
      return;
    }

    let cancelled = false;
    setStatus('loading');

    const viewSDKClient = new ViewSDKClient();

    const reportFailure = (error: unknown) => {
      if (cancelled) return;

      setStatus('error');
      datadogLogs.logger.error('Inspector document render failed', {
        error,
        proof_id: proofId,
        entry_id: entryId
      });
    };

    let rendered = false;
    const showWhenReady = () => {
      if (!cancelled && rendered && viewSDKClientRef.current === viewSDKClient) setStatus('ready');
    };

    const renderDocument = async () => {
      try {
        await viewSDKClient.ready();
        if (cancelled) return;

        await viewSDKClient.previewFile(
          viewerContainerId(proofId),
          VIEWER_CONFIG,
          file,
          cleanFileName(`${applicantName}_${fileName ?? ''}`),
          proofId,
          configVars.adobe.pdf_token,
          {
            onReady: () => {
              rendered = true;
              showWhenReady();
            },
            onFailed: () => reportFailure('APP_RENDERING_FAILED')
          }
        );
        if (cancelled) return;

        viewSDKClientRef.current = viewSDKClient;
        showWhenReady();
      } catch (error) {
        reportFailure(error);
      }
    };

    renderDocument();

    return () => {
      cancelled = true;
    };
  }, [file, fileName, proofId, applicantName, entryId]);

  const handleZoom = async (direction: 'in' | 'out') => {
    try {
      await (direction === 'in'
        ? viewSDKClientRef.current?.zoomIn()
        : viewSDKClientRef.current?.zoomOut());
    } catch (error) {
      datadogLogs.logger.error('Inspector document zoom failed', {
        error,
        proof_id: proofId,
        entry_id: entryId
      });
    }
  };
  const handleDownload = async () => {
    if (!file) return;
    await downloadDocument(file, fileName, proofId);
  };

  if (!currentProof) {
    return <DocumentStatusMessage role="status">No document selected.</DocumentStatusMessage>;
  }

  return (
    <DocumentSurface>
      <ViewerSurface
        key={proofId}
        id={viewerContainerId(proofId)}
        data-testid="inspector-document-viewer"
        $hidden={status !== 'ready'}
      />

      {status === 'loading' && (
        <DocumentStatusMessage role="status">Loading document…</DocumentStatusMessage>
      )}

      {status === 'unavailable' && (
        <DocumentStatusMessage role="status">
          This document is not available to view.
        </DocumentStatusMessage>
      )}

      {status === 'error' && (
        <DocumentStatusMessage role="status">
          This document could not be loaded. Try reloading the page.
        </DocumentStatusMessage>
      )}

      {status === 'ready' && showControls && (
        <ViewerControlsBelowHeader>
          <DocumentViewerControls
            onZoomIn={() => handleZoom('in')}
            onZoomOut={() => handleZoom('out')}
            onDownload={handleDownload}
          />
        </ViewerControlsBelowHeader>
      )}
    </DocumentSurface>
  );
};

export default InspectorDocument;
